import { useCallback, useEffect, useState } from 'react';
import { aiEngineeringAPI } from '@/lib/api';
import { useAuth } from '@/hooks/useAuth';

const DEFAULT_STATE = {
  loading: false,
  busy: false,
  enabled: false,
  source: 'default_off',
  updatedAt: null,
};

let cachedFeatureFlagState = null;

export const useAIEngineeringFeatureFlag = () => {
  const { user } = useAuth();
  const isAdminOnly = ['admin', 'super_admin'].includes(String(user?.role || '').trim().toLowerCase());
  const [state, setState] = useState(DEFAULT_STATE);

  const refresh = useCallback(async () => {
    if (!isAdminOnly) {
      setState(DEFAULT_STATE);
      return DEFAULT_STATE;
    }

    if (cachedFeatureFlagState) {
      setState(cachedFeatureFlagState);
      return cachedFeatureFlagState;
    }

    setState((current) => ({ ...current, loading: true }));
    const response = await aiEngineeringAPI.getFeatureFlag();
    const payload = response.data || {};
    const nextState = {
      loading: false,
      busy: false,
      enabled: Boolean(payload.enabled),
      source: payload.source || 'default_off',
      updatedAt: payload.updated_at || null,
    };
    cachedFeatureFlagState = nextState;
    setState(nextState);
    return nextState;
  }, [isAdminOnly]);

  const setEnabled = useCallback(async (enabled, justification) => {
    if (!isAdminOnly) return null;

    setState((current) => ({ ...current, busy: true }));
    try {
      const response = await aiEngineeringAPI.updateFeatureFlag({ enabled, justification });
      const payload = response.data || {};
      const nextState = {
        loading: false,
        busy: false,
        enabled: Boolean(payload.enabled),
        source: payload.source || 'database',
        updatedAt: payload.updated_at || null,
      };
      cachedFeatureFlagState = nextState;
      setState(nextState);
      return nextState;
    } catch (error) {
      setState((current) => ({ ...current, busy: false }));
      throw error;
    }
  }, [isAdminOnly]);

  useEffect(() => {
    if (!isAdminOnly) {
      setState(DEFAULT_STATE);
      return;
    }

    refresh().catch(() => {
      setState((current) => ({ ...current, loading: false }));
    });
  }, [isAdminOnly, refresh]);

  return {
    ...state,
    isAdminOnly,
    refresh,
    setEnabled,
  };
};